
import { useToast } from "@/components/ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";

interface ProfileAvatarProps {
  userId: string;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  onAvatarUpdate: (url: string) => void;
}

export const ProfileAvatar = ({
  userId,
  firstName,
  lastName,
  avatarUrl,
  onAvatarUpdate
}: ProfileAvatarProps) => {
  const { toast } = useToast();
  const { t } = useTranslations("profile");
  
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) return;
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${userId}/avatar.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userId);
      
      if (updateError) throw updateError;
      
      onAvatarUpdate(publicUrl);
      
      toast({
        title: t("photoSuccess"),
        description: t("photoSuccessDesc")
      });
    } catch (error) {
      toast({
        title: t("photoError"),
        description: t("photoErrorDesc"),
        variant: "destructive"
      });
    }
  };
  
  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="h-24 w-24">
        <AvatarImage src={avatarUrl} />
        <AvatarFallback>
          {firstName?.charAt(0)}{lastName?.charAt(0)}
        </AvatarFallback>
      </Avatar>
      <div className="flex items-center justify-center w-full">
        <Input 
          type="file" 
          accept="image/*" 
          onChange={handleAvatarUpload} 
          className="hidden" 
          id="avatar-upload" 
        />
        <Button 
          variant="outline" 
          onClick={() => document.getElementById('avatar-upload')?.click()} 
          className="w-full max-w-[200px] text-xs dark:text-white"
        >
          <Upload className="mr-2 h-4 w-4" />
          {t("uploadProfile")}
        </Button>
      </div>
    </div>
  );
};
